/* @flow */
import React from 'react'

import Basic from './basic'
import { fileListToArray } from '../../helpers/file'

import type { BasicPropsType, BasicStateType } from './basic'

export type InputPropsType = BasicPropsType & {
  type?: string,
  max?: number | string,
  min?: number | string,
  step?: number | string,
  pattern?: string,
  placeholder?: string,
  focused?: boolean,
  onBlur?: any,
  onFocus?: any,
  onKeyPress?: any,
  onKeyDown?: any,
  onKeyUp?: any,
  onClick?: any,
  disabled?: boolean,
  readOnly?: boolean,
  maxLength?: number | string,
  minLength?: number | string,
  tabIndex?: number,
  autoComplete?: string,
  autoCorrect?: string,
  autoCapitalize?: string,
  spellCheck?: string,
  inputClassName?: string
}

export type InputStateType = BasicStateType & {}

export class AbstractInput<Props: InputPropsType, State: InputStateType> extends Basic<Props, State> {
  static defaultProps = {
    ...Basic.defaultProps,
    inputClassName: 'atp-input__field'
  }

  componentDidMount(): void {
    if (this.getProps().focused) {
      this.focus()
    }
  }

  get value(): string | number {
    return super.value
  }

  onFocus = (event: SyntheticInputEvent<any>) => {
    const { onFocus } = this.getProps()
    if (typeof onFocus == 'function') onFocus(event)
    this.setState({ focus: true })
  }

  onBlur = (event: SyntheticInputEvent<any>) => {
    const { onBlur } = this.getProps()
    if (typeof onBlur == 'function') onBlur(event)
    this.setState({ focus: false })
  }

  formField(): React$Element<*> {
    const props = this.getProps()
    return (
      <input
        id={this.id}
        type={props.type || 'text'}
        className={props.inputClassName}
        placeholder={props.placeholder}
        value={String(this.value)}
        onChange={this.getValue}
        onKeyPress={props.onKeyPress}
        onKeyDown={props.onKeyDown}
        onKeyUp={props.onKeyUp}
        onClick={props.onClick}
        name={props.name}
        max={props.max}
        min={props.min}
        step={props.step}
        pattern={props.pattern}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        disabled={props.disabled}
        readOnly={props.readOnly}
        minLength={props.minLength}
        maxLength={props.maxLength}
        tabIndex={props.tabIndex}
        autoComplete={props.autoComplete}
        autoCorrect={props.autoCorrect}
        autoCapitalize={props.autoCapitalize}
        spellCheck={props.spellCheck}
        checked={props.checked}
        ref={this.inputRef}
      />
    )
  }
}

class TextInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'text' }
}

class EmailInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'email', rootType: 'email' }
}

class DateInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'date', rootType: 'native-date' }
}

class NumberInput extends AbstractInput<InputPropsType, InputStateType> {
  static defaultProps = {
    ...AbstractInput.defaultProps,
    type: 'number',
    pattern: '[0-9]*',
    rootType: 'number'
  }
  props: InputPropsType

  handleChange(value: number | string) {
    const { onChange, maxLength } = this.getProps()
    if (value === '') {
      if (onChange) onChange(value)
      return
    }
    let newValue = Number(value)
    if (maxLength && !isNaN(newValue)) {
      newValue = Number(String(newValue).substring(0, Number(maxLength)))
    }
    if (onChange) onChange(newValue)
  }
}

class TelInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'tel', rootType: 'tel' }
}

class TimeInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'time', rootType: 'time' }
}

class SearchInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'search', rootType: 'search' }
}

class UrlInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'url', rootType: 'url' }
}

class PasswordInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'password', rootType: 'password' }
}

class InputOnly extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType

  render(): React$Element<*> {
    return this.formField()
  }
}

class FileInput extends AbstractInput<InputPropsType, InputStateType> {
  props: InputPropsType
  static defaultProps = { ...AbstractInput.defaultProps, type: 'file', rootType: 'file' }

  getValue = (event: SyntheticInputEvent<any>): void => {
    super.handleChange(fileListToArray(event.target.files)[0])
  }

  get value(): any {
    return null
  }
}

class Input extends AbstractInput<InputPropsType, InputStateType> {}

export default Input

export {
  TextInput,
  EmailInput,
  DateInput,
  NumberInput,
  TelInput,
  TimeInput,
  SearchInput,
  UrlInput,
  PasswordInput,
  InputOnly,
  FileInput
}
